import { maxBy, minBy } from 'es-toolkit'
import type { PrecipitationAlert } from '../../data/alerts/types.ts'
import type { ForecastPrecipitationProtection } from '../../data/protections/types.ts'
import { type Dayjs, dayjs } from '../../dayjs.ts'

export const precipitationAccumulationFields = ['accum1hr', 'accum6hr', 'accum24hr', 'accum72hr'] as const
export type PrecipitationAccumulationField = (typeof precipitationAccumulationFields)[number]

export const accumulationParameters = {
	accum1hr: 'PRECIPITATION_LIQUID_ACCUM_1HR_MM',
	accum6hr: 'PRECIPITATION_LIQUID_ACCUM_6HR_MM',
	accum24hr: 'PRECIPITATION_LIQUID_ACCUM_24HR_MM',
	accum72hr: 'PRECIPITATION_LIQUID_ACCUM_72HR_MM',
} as const satisfies Record<PrecipitationAccumulationField, string>

// Alerts from protections saved before the liquid accumulations carry the total ones instead.
// TODO: Remove this after QA cycle. All new protections won't carry these.
const totalAccumulationParameters = {
	accum1hr: 'PRECIPITATION_ACCUM_1HR_MM',
	accum6hr: 'PRECIPITATION_ACCUM_6HR_MM',
	accum24hr: 'PRECIPITATION_ACCUM_24HR_MM',
	accum72hr: 'PRECIPITATION_ACCUM_72HR_MM',
} as const satisfies Record<PrecipitationAccumulationField, string>

type PrecipitationContext = PrecipitationAlert['context'][number]

const toNumber = (value: string | undefined) => {
	if (value == null) return undefined
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : undefined
}

export const accumulationAt = (context: PrecipitationContext, field: PrecipitationAccumulationField) =>
	toNumber(context.alertContextValue[accumulationParameters[field]] ?? context.alertContextValue[totalAccumulationParameters[field]])

export const thresholdOf = (protection: ForecastPrecipitationProtection, field: PrecipitationAccumulationField) =>
	protection[accumulationParameters[field]]

export const configuredWindows = (protection: ForecastPrecipitationProtection) =>
	precipitationAccumulationFields.filter((field) => thresholdOf(protection, field) != null)

export type PrecipitationBreach = {
	field: PrecipitationAccumulationField
	value: number
	threshold: number
	exceedance: number
}

const breachOf = (value: number | undefined, threshold: number | undefined, field: PrecipitationAccumulationField) => {
	if (value == null || threshold == null || value < threshold) return undefined
	return { field, value, threshold, exceedance: value / threshold }
}

export const breachesAt = (context: PrecipitationContext, protection: ForecastPrecipitationProtection): PrecipitationBreach[] =>
	precipitationAccumulationFields.flatMap((field) => breachOf(accumulationAt(context, field), thresholdOf(protection, field), field) ?? [])

export const onsetBreach = (contexts: PrecipitationContext[], protection: ForecastPrecipitationProtection) => {
	const breaching = contexts.flatMap((context) => {
		const worst = maxBy(breachesAt(context, protection), (breach) => breach.exceedance)
		return worst ? [{ time: context.time, ...worst }] : []
	})
	return minBy(breaching, ({ time }) => dayjs(time).valueOf())
}

export type PrecipitationBin = {
	id: string
	fromHour: number
	toHour: number
	start: string
	end: string
	pop: number | undefined
	peak: Partial<Record<PrecipitationAccumulationField, number>>
}

export const binSpansMultipleHours = (bin: PrecipitationBin) => bin.toHour > bin.fromHour

const BIN_SPEC = [
	{ untilHour: 5, sizeHours: 1 },
	{ untilHour: 23, sizeHours: 3 },
	{ untilHour: 47, sizeHours: 6 },
	{ untilHour: 71, sizeHours: 12 },
]

const binRanges = () => {
	const ranges: { fromHour: number; toHour: number }[] = []
	let fromHour = 0
	for (const { untilHour, sizeHours } of BIN_SPEC) {
		while (fromHour <= untilHour) {
			ranges.push({ fromHour, toHour: Math.min(fromHour + sizeHours - 1, untilHour) })
			fromHour += sizeHours
		}
	}
	return ranges
}

const highest = (values: (number | undefined)[]) => {
	const present = values.filter((v): v is number => v != null && Number.isFinite(v))
	return present.length > 0 ? Math.max(...present) : undefined
}

const binAnchor = (contexts: PrecipitationContext[], now: Dayjs) => {
	const earliest = minBy(contexts, (context) => dayjs(context.time).valueOf())
	const currentHour = now.startOf('hour')
	if (!earliest) return currentHour
	const earliestHour = dayjs(earliest.time).startOf('hour')
	return earliestHour.isBefore(currentHour) ? earliestHour : currentHour
}

/**
 * Groups the hours an alert fired on into progressively coarser bins. Each bin reports the highest
 * value in its range per window. Bins no hour fell into are dropped, so the list only shows times
 * that actually triggered.
 */
export const toPrecipitationBins = (contexts: PrecipitationContext[], now = dayjs()): PrecipitationBin[] => {
	const anchor = binAnchor(contexts, now)

	return binRanges().flatMap(({ fromHour, toHour }) => {
		const start = anchor.add(fromHour, 'hour')
		const end = anchor.add(toHour + 1, 'hour')
		const inBin = contexts.filter((context) => {
			const time = dayjs(context.time)
			return !time.isBefore(start) && time.isBefore(end)
		})
		if (inBin.length === 0) return []

		return [
			{
				id: `${fromHour}-${toHour}`,
				fromHour,
				toHour,
				start: start.toISOString(),
				end: end.toISOString(),
				pop: highest(inBin.map((context) => toNumber(context.alertContextValue.PRECIPITATION_POP))),
				peak: Object.fromEntries(
					precipitationAccumulationFields.flatMap((field) => {
						const value = highest(inBin.map((context) => accumulationAt(context, field)))
						return value == null ? [] : [[field, value]]
					}),
				) as PrecipitationBin['peak'],
			},
		]
	})
}
